/**
 * Campañas de Meta vistas desde el CRM: periodos, agrupación por campaña y
 * cifras para la pantalla. Las cuentas las hace `crm_campanas` en la base de
 * datos; aquí solo se ordenan y se formatean.
 */
import { diaMadrid, sumarDias } from "./meta";

/** Una fila de `crm_campanas`. Importes en céntimos. */
export interface FilaCampana {
  nivel: "total" | "campana" | "anuncio";
  campaign_id: string | null;
  campaign_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  gasto_cents: number;
  impresiones: number;
  clics: number;
  leads_meta: number;
  leads_crm: number;
  contactados: number;
  propuestas: number;
  reservados: number;
  contratado_cents: number;
  cobrado_cents: number;
  coste_lead_cents: number | null;
  coste_boda_cents: number | null;
  roas: number | null;
}

export interface CampanaConAnuncios {
  campana: FilaCampana;
  anuncios: FilaCampana[];
}

export interface ResumenCampanas {
  total: FilaCampana | null;
  campanas: CampanaConAnuncios[];
}

/** PostgREST devuelve bigint y numeric como texto o número según el tamaño: todo a número. */
export function normalizarFilaCampana(fila: Record<string, unknown>): FilaCampana {
  const numero = (valor: unknown) => Number(valor ?? 0);
  const opcional = (valor: unknown) =>
    valor === null || valor === undefined ? null : Number(valor);
  return {
    nivel: fila.nivel as FilaCampana["nivel"],
    campaign_id: (fila.campaign_id as string | null) ?? null,
    campaign_name: (fila.campaign_name as string | null) ?? null,
    ad_id: (fila.ad_id as string | null) ?? null,
    ad_name: (fila.ad_name as string | null) ?? null,
    gasto_cents: numero(fila.gasto_cents),
    impresiones: numero(fila.impresiones),
    clics: numero(fila.clics),
    leads_meta: numero(fila.leads_meta),
    leads_crm: numero(fila.leads_crm),
    contactados: numero(fila.contactados),
    propuestas: numero(fila.propuestas),
    reservados: numero(fila.reservados),
    contratado_cents: numero(fila.contratado_cents),
    cobrado_cents: numero(fila.cobrado_cents),
    coste_lead_cents: opcional(fila.coste_lead_cents),
    coste_boda_cents: opcional(fila.coste_boda_cents),
    roas: opcional(fila.roas),
  };
}

/** Cada campaña con sus anuncios, en el orden de gasto que trae la base de datos. */
export function agruparCampanas(filas: readonly FilaCampana[]): ResumenCampanas {
  return {
    total: filas.find((f) => f.nivel === "total") ?? null,
    campanas: filas
      .filter((f) => f.nivel === "campana")
      .map((campana) => ({
        campana,
        anuncios: filas.filter(
          (f) => f.nivel === "anuncio" && f.campaign_id === campana.campaign_id,
        ),
      })),
  };
}

/* --- Periodos ---------------------------------------------------------- */

export type PeriodoCampanas = "7d" | "30d" | "mes" | "todo";

export const ETIQUETA_PERIODO: Record<PeriodoCampanas, string> = {
  "7d": "7 días",
  "30d": "30 días",
  mes: "Este mes",
  todo: "Desde el inicio",
};

export interface RangoFechas {
  /** AAAA-MM-DD, o null para no poner límite. */
  desde: string | null;
  hasta: string;
}

/** Días de Madrid, ambos incluidos. "7 días" son hoy y los 6 anteriores. */
export function rangoPeriodo(periodo: PeriodoCampanas, ahora: Date): RangoFechas {
  const hoy = diaMadrid(ahora);
  switch (periodo) {
    case "7d":
      return { desde: sumarDias(hoy, -6), hasta: hoy };
    case "30d":
      return { desde: sumarDias(hoy, -29), hasta: hoy };
    case "mes":
      return { desde: `${hoy.slice(0, 8)}01`, hasta: hoy };
    case "todo":
      return { desde: null, hasta: hoy };
  }
}

/* --- Textos ------------------------------------------------------------ */

/** Nombre de la campaña o del anuncio; si Meta aún no lo ha dado, su id. */
export function nombreFila(fila: FilaCampana): string {
  if (fila.nivel === "anuncio") return fila.ad_name ?? `Anuncio ${fila.ad_id ?? "sin id"}`;
  return fila.campaign_name ?? (fila.campaign_id ? `Campaña ${fila.campaign_id}` : "Sin campaña");
}

/** "2,4×" o "–" si no hay gasto. */
export function textoRoas(roas: number | null): string {
  if (roas === null) return "–";
  return `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 }).format(roas)}×`;
}

/** "hace 4 min", "hace 2 h", "hace 3 días". */
export function textoHace(desde: string, ahora: Date): string {
  const minutos = Math.max(Math.floor((ahora.getTime() - Date.parse(desde)) / 60_000), 0);
  if (minutos < 1) return "hace un momento";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? "hace 1 día" : `hace ${dias} días`;
}

/** Estado guardado por la última pasada de la sincronización (`meta_sync_state.sync`). */
export interface UltimaSincronizacion {
  at: string;
  leads_nuevos: number;
  metricas: string;
  errores: string[];
}
